import React from "react";
import { COLORS, Tooltip } from "@bikdotai/bik-component-library";
import styled, { css } from "styled-components";

export interface CommonTableI {
  tableHeadData: string[];
  tableData: CommonTableMainDataI[];
  rowStyles?: { index: number; styles: React.CSSProperties }[];
  isBik?: boolean;
  appplyAnalyticsStyle?: boolean;
  tdEllipsisMaxWidth?: string;
  stickyActions?: boolean;
  columnWidths?: { [columnName: string]: string };
  rowHeight?: string;
}

export type CommonTableMainDataI = {
  [key: string]: JSX.Element | string;
};

const CommonTable: React.FC<CommonTableI> = ({
  tableHeadData,
  tableData,
  rowStyles,
  isBik,
  appplyAnalyticsStyle,
  tdEllipsisMaxWidth,
  stickyActions = false,
  columnWidths,
  rowHeight,
}) => {
  const scrollWrapperRef = React.useRef<HTMLDivElement>(null);
  
  React.useEffect(() => {
    const wrapper = scrollWrapperRef.current;
    if (!wrapper) return;
    
    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = wrapper;
      const isScrolledToEnd = scrollLeft + clientWidth >= scrollWidth - 5; // 5px threshold
      wrapper.setAttribute('data-scrolled-to-end', String(isScrolledToEnd));
    };
    
    // Initial check
    handleScroll();
    
    // Add scroll listener
    wrapper.addEventListener('scroll', handleScroll);
    
    // Check on resize
    window.addEventListener('resize', handleScroll);
    
    return () => {
      wrapper.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [tableData]);
  
  const eachKeyOfTableData = Object.keys(tableData[0]);
  const isActionsColumn = (index: number) => {
    return stickyActions && tableHeadData[index]?.toLowerCase() === "actions";
  };

  const getColumnWidth = (columnName: string) => {
    return columnWidths?.[columnName];
  };

  return (
    <ScrollableTableWrapper ref={scrollWrapperRef}>
      <CommonTableContainer
        isBik={isBik}
        appplyAnalyticsStyle={appplyAnalyticsStyle}
        tdEllipsisMaxWidth={tdEllipsisMaxWidth}
        stickyActions={stickyActions}
        rowHeight={rowHeight}
      >
        {tableHeadData && tableHeadData.length ? (
          <thead>
            <tr>
              {tableHeadData.map((item, index) => (
                <th
                  key={index}
                  className={isActionsColumn(index) ? "sticky-actions" : ""}
                  style={{
                    width: getColumnWidth(item),
                    minWidth: getColumnWidth(item) || "auto",
                    maxWidth: getColumnWidth(item) || "none",
                  }}
                >
                  {item}
                </th>
              ))}
            </tr>
          </thead>
        ) : (
          <></>
        )}
        <tbody>
          {tableData.map((item, rowIndex) => (
            <tr key={rowIndex} style={getRowStyle(rowIndex)}>
              {eachKeyOfTableData.map((key, colIndex) => (
                <td
                  key={colIndex}
                  className={`td-class ${isActionsColumn(colIndex) ? "sticky-actions" : ""}`}
                  style={{
                    ...getRowStyle(rowIndex),
                    width: getColumnWidth(tableHeadData[colIndex]),
                    minWidth: getColumnWidth(tableHeadData[colIndex]) || "auto",
                    maxWidth: getColumnWidth(tableHeadData[colIndex]) || "none",
                  }}
                  title={
                    typeof item[key] === "object" ? "" : item[key]?.toString()
                  }
                >
                  <div
                    className={`${isBik ? "left--style" : ""} ${appplyAnalyticsStyle && tableHeadData[colIndex] !== "Feature Title" && tableHeadData[colIndex] !== "Feature Description" ? "ellipsis__td" : ""}`}
                  >
                    {typeof item[key] === "string" ? (
                      <Tooltip
                        tooltipContent={
                          <div
                            dangerouslySetInnerHTML={{
                              __html: item[key].toString(),
                            }}
                          ></div>
                        }
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              typeof item[key] === "string"
                                ? item[key].toString()
                                : "",
                          }}
                        ></div>
                      </Tooltip>
                    ) : (
                      item[key]
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </CommonTableContainer>
    </ScrollableTableWrapper>
  );

  function getRowStyle(rowIndex: number): React.CSSProperties | undefined {
    if (rowStyles) {
      const styleObject = rowStyles.find((style) => style.index === rowIndex);
      return styleObject?.styles;
    }
    return undefined;
  }
};

export default CommonTable;

const ScrollableTableWrapper = styled.div`
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  
  /* Webkit scrollbar styling for better visibility */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${COLORS.surface.subdued};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${COLORS.stroke.primary};
    border-radius: 4px;
    
    &:hover {
      background: ${COLORS.content.secondary};
    }
  }
  
  /* Scroll indicator shadow on the right when content overflows */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 30px;
    background: linear-gradient(to left, rgba(0, 0, 0, 0.1), transparent);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  /* Show scroll indicator when scrollable */
  @media (max-width: 1024px) {
    &::after {
      opacity: 1;
    }
  }
  
  /* Hide indicator when scrolled to the end */
  &:has(table) {
    &[data-scrolled-to-end="true"]::after {
      opacity: 0;
    }
  }
`;

export const CommonTableContainer = styled.table<{
  isBik?: boolean;
  appplyAnalyticsStyle?: boolean;
  tdEllipsisMaxWidth?: string;
  stickyActions?: boolean;
  rowHeight?: string;
}>`
  width: 100%;
  table-layout: auto;
  background-color: ${({ appplyAnalyticsStyle }) =>
    appplyAnalyticsStyle ? "#ffffff" : ""};
  border-radius: 8px;
  min-width: 800px; /* Ensure minimum width for horizontal scroll */

  thead {
    height: 44px;
    background: ${({ appplyAnalyticsStyle }) =>
      appplyAnalyticsStyle
        ? COLORS.surface.subdued
        : COLORS.background.brandLight};
    flex: none;
    order: 2;
    flex-grow: 0;
    th {
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      color: ${({ appplyAnalyticsStyle }) =>
        appplyAnalyticsStyle
          ? COLORS.content.primary
          : COLORS.background.brand};
      padding: 12px 12px;
      border-bottom: ${({ appplyAnalyticsStyle }) =>
        appplyAnalyticsStyle ? `1px solid ${COLORS.stroke.primary}` : "none"};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.sticky-actions {
        position: sticky;
        right: 0;
        background: ${({ appplyAnalyticsStyle }) =>
          appplyAnalyticsStyle
            ? COLORS.surface.subdued
            : COLORS.background.brandLight};
        z-index: 2;
        box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
      }
    }
  }
  tr {
    border-bottom: 1px solid ${COLORS.stroke.primary};
    min-height: ${({ rowHeight }) => rowHeight || "auto"};
    td {
      padding: 12px 12px;
      vertical-align: top;
      min-height: ${({ rowHeight }) => rowHeight || "auto"};
      line-height: 1.4;

      &.sticky-actions {
        position: sticky;
        right: 0;
        background: white;
        z-index: 1;
        box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
      }
    }
  }
  .columnTable {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .rowTable {
    display: flex;
    flex-direction: row;
    > * {
      margin-right: 24px;
    }
  }
  .alignRight {
    text-align: right;
  }
  th:first-child {
    border-radius: 8px 0px 0px 0px;
  }
  th:last-child {
    border-radius: 0px 8px 0px 0px;
  }
  td:first-child {
    ${({ isBik, appplyAnalyticsStyle }) => css`
      min-width: ${isBik && !appplyAnalyticsStyle ? "339px" : "unset"};
      max-width: ${isBik && !appplyAnalyticsStyle ? "283px" : "unset"};
    `}
    .left--style {
      display: flex;
      justify-content: flex-start;
    }
  }

  td {
    .left--style {
      display: flex;
      justify-content: center;
    }
  }
  tr:last-child {
    border-bottom: none;
  }
  .td-class {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    color: ${COLORS.content.secondary};
  }
  .ellipsis__td {
    max-width: 200px !important;
    max-width: ${({ tdEllipsisMaxWidth }) =>
      tdEllipsisMaxWidth ?? "200px"}!important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
